import { memo, useId } from "react";

import { Box, FormControlLabel, Switch, Typography } from "@mui/material";
import { Tooltip } from "@shared/Tooltip";

import { useStyles } from "./WorkspaceConfigurationPanel.style";

type WorkspaceSettingRowProps = {
    name: API.WorkspaceSettingName;
    label: string;
    value: 0 | 1;
    isBusy: boolean;
    disabledReason?: string;
    // Id of the card heading. Every card repeats the same "show this page in the navigation" label, so
    // without it a screen reader announces a row that could belong to any of the pages.
    labelledByCardId?: string;
    onToggle: (name: API.WorkspaceSettingName, nextChecked: boolean) => void;
};

const WorkspaceSettingRowComponent = (props: WorkspaceSettingRowProps) => {
    const { name, label, value, isBusy, disabledReason, labelledByCardId, onToggle } = props;
    const { classes, cx } = useStyles();
    const labelId = useId();

    const isDisabled = disabledReason !== undefined;
    const showShield = name === "MembersRequireTwoFactorAuthentication";

    return (
        <Tooltip title={disabledReason} hide={!isDisabled}>
            <FormControlLabel
                className={classes.control}
                labelPlacement="start"
                control={
                    <Switch
                        checked={value === 1}
                        disabled={isDisabled || isBusy}
                        slotProps={
                            labelledByCardId ? { input: { "aria-labelledby": `${labelledByCardId} ${labelId}` } } : undefined
                        }
                        onChange={(_e, nextChecked) => {
                            onToggle(name, nextChecked);
                        }}
                    />
                }
                label={
                    <Box className={cx(classes.label, isDisabled && classes.labelDisabled)}>
                        <Typography id={labelId} variant="body1" color="inherit" component="span">
                            {label}
                        </Typography>
                        {showShield ? <i className={cx("fa-solid fa-shield-halved", classes.shieldIcon)} aria-hidden /> : null}
                    </Box>
                }
            />
        </Tooltip>
    );
};

export const WorkspaceSettingRow = memo(WorkspaceSettingRowComponent);
